import React, { useState } from 'react';
import { useRole } from '../../hooks/useRole';
import EmptyState from '../EmptyState';
import UserManagement from '../UserManagement';
import AccountBar from './AccountBar';
import type { AccountView } from '../../utils/accountRoutes';
import BreadcrumbBar, { type BreadcrumbAction } from '../BreadcrumbBar';
import PeopleDirectory from '../people/PeopleDirectory';
import { LOGIN_FILTER_OPTIONS, peopleFilterOf, type LoginFilter } from '../people/peopleView';
import WorkspaceSettingsSections from '../settings/WorkspaceSettingsSections';
import '../../styles/components/Settings.css';

const NoAccess: React.FC<{ who: string; view: AccountView }> = ({ who, view }) => (
  <div className="wt-container fade-in ws-admin-page">
    <AccountBar view={view} />
    <EmptyState
      icon="shield"
      title="You don't have access to this page"
      description={`Only ${who} can open it. Ask an owner if you need something changed.`}
    />
  </div>
);

/**
 * Workspace Settings, mounted at account level: what is set once for the whole workspace (Billing, Vendor
 * categories, WhatsApp Business). Owners see all of it; an admin sees WhatsApp only. The per-project screen
 * is Settings.tsx. The page title is the shell's header title.
 */
export const WorkspaceSettingsPage: React.FC = () => {
  const { isOwner, isAdmin } = useRole();
  if (!isAdmin) return <NoAccess who="an owner or admin" view="workspace-settings" />;
  return (
    <div className="wt-container fade-in ws-admin-page">
      <AccountBar view="workspace-settings" />
      <WorkspaceSettingsSections isOwner={isOwner} />
    </div>
  );
};

/**
 * Team: everyone in the workspace (the Team and contacts directory) and, below it, their logins and roles
 * (UserManagement). The two lists show the same accounts, so a change in one reloads the other (a new key
 * starts it fresh).
 */
export const WorkspaceTeamPage: React.FC = () => {
  const { isAdmin, isOwner } = useRole();
  const [peopleKey, setPeopleKey] = useState(0);
  const [loginsKey, setLoginsKey] = useState(0);
  // The bar's buttons raise these counters; the lists below open their form when one goes up.
  const [addSignal, setAddSignal] = useState(0);
  const [inviteSignal, setInviteSignal] = useState(0);
  // Archived and Login status live in the bar (the page body has no tab row of its own).
  const [archived, setArchived] = useState(false);
  const [login, setLogin] = useState<LoginFilter>('all');
  if (!isAdmin) return <NoAccess who="an admin or owner" view="workspace-team" />;

  // Adding a contact needs an admin, inviting a login needs an owner (the API refuses everyone else).
  const barActions: BreadcrumbAction[] = [
    // The Archived list is not split by login, so the Login status choice only applies to the live list.
    {
      kind: 'filters', key: 'filters', visible: !archived,
      selects: [{
        key: 'login', label: 'Login status', options: [...LOGIN_FILTER_OPTIONS], value: login, defaultValue: 'all',
        onChange: v => setLogin(v as LoginFilter),
      }],
    },
    { kind: 'primary', key: 'add-person', icon: 'plus', label: 'Add person', onClick: () => setAddSignal(n => n + 1) },
    ...(isOwner ? [{ kind: 'button' as const, key: 'invite', icon: 'mail', label: 'Invite', onClick: () => setInviteSignal(n => n + 1) }] : []),
  ];

  return (
    <div className="wt-container fade-in ws-admin-page">
      <AccountBar
        view="workspace-team"
        archived={{ checked: archived, onChange: setArchived }}
        actions={<BreadcrumbBar actions={barActions} />}
        exportKey="account-people"
      />
      <PeopleDirectory key={`people-${peopleKey}`} filter={peopleFilterOf(archived, login)} openAddSignal={addSignal} onChanged={() => setLoginsKey(k => k + 1)} />
      <UserManagement key={`logins-${loginsKey}`} inviteSignal={inviteSignal} onChanged={() => setPeopleKey(k => k + 1)} />
    </div>
  );
};
